import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  Tabs,
  Tab,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

// import { useAuth } from '../context/AuthContext'; // Not needed currently
import { useTenant } from '../context/TenantContext';
import { patientService, Patient, PatientUpdate } from '../services/patientService';
import { meetingService, Meeting } from '../services/meetingService';
import { meetingTextService, MeetingText } from '../services/meetingTextService';
import { activityLogService, ActivityLog, ActivityType } from '../services/activityLogService';
import { fileService, File as FileType } from '../services/fileService';
import { formatDateTime, formatDate } from '../utils/dateUtils';

const PatientDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // const { } = useAuth(); // Not needed currently
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  // UI State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [summariesDialogOpen, setSummariesDialogOpen] = useState(false);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [allSummaries, setAllSummaries] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  // Tab State
  const [currentTab, setCurrentTab] = useState(0);

  // Activity Log State
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState<string | null>(null);
  const [activityLogSearch, setActivityLogSearch] = useState('');
  const [activityLogType, setActivityLogType] = useState<ActivityType | ''>('');
  const [activityLogPage, setActivityLogPage] = useState(0);
  const [activityLogRowsPerPage, setActivityLogRowsPerPage] = useState(25);
  const [activityLogTotal, setActivityLogTotal] = useState(0);

  // Files State
  const [files, setFiles] = useState<FileType[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Load patient and meetings data
  useEffect(() => {
    const loadPatientData = async () => {
      if (!id || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const patientId = parseInt(id);
        const [patientData, meetingsData] = await Promise.all([
          patientService.getPatient(patientId),
          meetingService.getMeetings(currentTenant.id, patientId, undefined, 'all', 'all'),
        ]);

        setPatient(patientData);
        setMeetings(meetingsData);
      } catch (err) {
        console.error('Error loading patient data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load patient data');
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [id, currentTenant]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<PatientUpdate>();


  const handleEditPatient = () => {
    if (patient) {
      reset({
        first_name: patient.first_name,
        last_name: patient.last_name,
        email: patient.email || '',
        phone: patient.phone || '',
        date_of_birth: patient.date_of_birth,
        emergency_contact_name: patient.emergency_contact_name || '',
        emergency_contact_phone: patient.emergency_contact_phone || '',
        notes: patient.notes || '',
        insurance_provider: patient.insurance_provider || '',
        insurance_id: patient.insurance_id || '',
        status: patient.status,
      });
      setEditDialogOpen(true);
    }
  };

  const onSubmitPatientEdit = async (data: PatientUpdate) => {
    if (!patient) {
      toast.error('No patient data available');
      return;
    }

    try {
      // Clean up empty strings to null for optional fields
      const patientData = {
        ...data,
        date_of_birth: data.date_of_birth || null,
        email: data.email || null,
      };

      const updatedPatient = await patientService.updatePatient(patient.id, patientData);
      setPatient(updatedPatient);

      toast.success('Patient updated successfully');
      setEditDialogOpen(false);
      reset();
    } catch (error) {
      console.error('Error updating patient:', error);
      toast.error('Failed to update patient');
    }
  };

  const handleScheduleMeeting = () => {
    if (patient) {
      navigate(`/meetings?patient_id=${patient.id}&action=schedule`);
    }
  };

  // Fetch activity logs for the patient
  useEffect(() => {
    const fetchActivityLogs = async () => {
      if (!patient || currentTab !== 2) return;

      setActivityLogsLoading(true);
      setActivityLogsError(null);
      try {
        const response = await activityLogService.getActivityLogs({
          page: activityLogPage + 1,
          page_size: activityLogRowsPerPage,
          search: activityLogSearch || undefined,
          activity_type: activityLogType || undefined,
          sort_by: 'created_at',
          sort_order: 'desc',
        });

        // Filter logs related to this patient
        const patientLogs = response.items.filter(
          log => log.patient_id === patient.id
        );

        setActivityLogs(patientLogs);
        setActivityLogTotal(patientLogs.length);
      } catch (err: unknown) {
        const error = err as Error;
        setActivityLogsError(error.message || 'Failed to fetch activity logs');
      } finally {
        setActivityLogsLoading(false);
      }
    };

    fetchActivityLogs();
  }, [patient, currentTab, activityLogPage, activityLogRowsPerPage, activityLogSearch, activityLogType]);

  const handleViewAllSummaries = async () => {
    if (!patient) return;

    try {
      setSummariesLoading(true);
      setSummariesDialogOpen(true);

      // Sort meetings by date descending
      const sortedMeetings = [...meetings].sort(
        (a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
      );

      // Fetch summaries for all meetings
      const summariesData: { meeting: Meeting; texts: MeetingText[] }[] = [];

      for (const meeting of sortedMeetings) {
        try {
          const texts = await meetingTextService.getMeetingTexts(meeting.id, 'summary', false);
          if (texts.length > 0) {
            summariesData.push({ meeting, texts });
          }
        } catch (error) {
          console.error(`Error fetching summaries for meeting ${meeting.id}:`, error);
        }
      }

      // Format all summaries into a single text
      let formattedText = `Meeting Summaries for ${patient.full_name}\n`;
      formattedText += `Generated on: ${formatDateTime(new Date())}\n`;
      formattedText += `\n${'='.repeat(80)}\n\n`;

      for (const { meeting, texts } of summariesData) {
        formattedText += `Meeting Date: ${formatDateTime(meeting.scheduled_at)}\n`;
        formattedText += `Meeting: ${meeting.title}\n`;
        formattedText += `Therapist: ${meeting.therapist.full_name}\n`;
        formattedText += `\n${'-'.repeat(80)}\n\n`;

        // Group texts by title and take the non-AI generated one if both exist
        const textsByTitle = new Map<string, MeetingText>();

        for (const text of texts) {
          const existing = textsByTitle.get(text.title);
          if (!existing) {
            textsByTitle.set(text.title, text);
          } else {
            // Prefer non-AI generated (edited) summaries over AI generated ones
            if (!text.is_ai_generated && existing.is_ai_generated) {
              textsByTitle.set(text.title, text);
            }
          }
        }

        // Add each section
        for (const text of Array.from(textsByTitle.values())) {
          formattedText += `${text.title}:\n`;
          formattedText += `${text.content}\n\n`;
        }

        formattedText += `${'='.repeat(80)}\n\n`;
      }

      if (summariesData.length === 0) {
        formattedText += 'No meeting summaries found.\n';
      }

      setAllSummaries(formattedText);
    } catch (error) {
      console.error('Error fetching summaries:', error);
      toast.error('Failed to load summaries');
    } finally {
      setSummariesLoading(false);
    }
  };

  const handleDownloadSummaries = () => {
    if (!patient || !allSummaries) return;

    const blob = new Blob([allSummaries], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${patient.full_name.replace(/\s+/g, '_')}_summaries_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Summaries downloaded successfully');
  };

  // Fetch files for the patient
  useEffect(() => {
    const fetchFiles = async () => {
      if (!patient || currentTab !== 1) return;

      setFilesLoading(true);
      try {
        const response = await fileService.getFiles(patient.id, undefined);
        setFiles(response.items);
      } catch (error) {
        console.error('Error fetching files:', error);
        toast.error('Failed to load files');
      } finally {
        setFilesLoading(false);
      }
    };

    fetchFiles();
  }, [patient, currentTab]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0] || !patient) return;

    const file = event.target.files[0];
    setUploadingFile(true);

    try {
      await fileService.uploadFile(file, undefined, patient.id, undefined);
      toast.success('File uploaded successfully');

      // Refresh files list
      const response = await fileService.getFiles(patient.id, undefined);
      setFiles(response.items);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleFileDownload = async (fileId: number) => {
    try {
      await fileService.downloadFile(fileId);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (fileId: number) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await fileService.deleteFile(fileId);
      toast.success('File deleted successfully');

      // Refresh files list
      if (patient) {
        const response = await fileService.getFiles(patient.id, undefined);
        setFiles(response.items);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  // Meeting menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleViewDetails = () => {
    if (selectedMeeting) {
      navigate(`/meetings/${selectedMeeting.id}`);
    }
    handleMenuClose();
  };

  const handleEditMeeting = () => {
    if (selectedMeeting) {
      navigate(`/meetings?edit=${selectedMeeting.id}`);
    }
    handleMenuClose();
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedMeeting) return;

    try {
      await meetingService.updateMeeting(selectedMeeting.id, { status: newStatus as any });

      // Update local state
      setMeetings(meetings.map(m =>
        m.id === selectedMeeting.id
          ? { ...m, status: newStatus as any }
          : m
      ));
      toast.success(`Meeting marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast.error('Failed to update meeting status');
    }
    handleStatusMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'done':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'noshow':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Group meetings by status
  const activeMeetings = meetings.filter(m => m.status === 'active');
  const completedMeetings = meetings.filter(m => m.status === 'done');
  const otherMeetings = meetings.filter(m => !['active', 'done'].includes(m.status));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading patient data: {error}
      </Alert>
    );
  }

  if (!patient) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Patient not found
      </Alert>
    );
  }

  return (
    <Box sx={{
      px: { xs: 0.5, sm: 2, md: 3 },
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      {/* Patient Header */}
      <Paper sx={{
        p: { xs: 1.5, sm: 3 },
        mb: 3,
        mx: { xs: 0.5, sm: 0 }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 3, md: 0 }}>
          <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', sm: 'left' }}>
            <Avatar sx={{ width: { xs: 80, sm: 64 }, height: { xs: 80, sm: 64 }, mr: { xs: 0, sm: 3 }, mb: { xs: 2, sm: 0 }, bgcolor: 'primary.main' }}>
              <PersonIcon sx={{ fontSize: { xs: 40, sm: 32 } }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }} gutterBottom>
                {patient.full_name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Patient ID: #{patient.id}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <Chip
                  label={patient.status.toUpperCase()}
                  color={patient.status === 'active' ? 'success' : 'default'}
                />
              </Box>
            </Box>
          </Box>
          <Box display="flex" gap={2} alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', md: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleScheduleMeeting}
              disabled={!currentTenant}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                padding: { xs: '6px 16px', sm: '8px 22px' }
              }}
            >
              Schedule Meeting
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleViewAllSummaries}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                padding: { xs: '6px 16px', sm: '8px 22px' }
              }}
            >
              View All Summaries
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditPatient}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                padding: { xs: '6px 16px', sm: '8px 22px' }
              }}
            >
              Edit Patient
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Patient Information */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {patient.email && (
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="action" />
                  <Typography>{patient.email}</Typography>
                </Box>
              )}
              {patient.phone && (
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" />
                  <Typography>{patient.phone}</Typography>
                </Box>
              )}
              {patient.date_of_birth && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon color="action" />
                  <Typography>Born: {formatDate(patient.date_of_birth)}</Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Medical Information
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {patient.emergency_contact_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Emergency Contact
                  </Typography>
                  <Typography>
                    {patient.emergency_contact_name}
                    {patient.emergency_contact_phone && ` - ${patient.emergency_contact_phone}`}
                  </Typography>
                </Box>
              )}
              {patient.insurance_provider && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Insurance
                  </Typography>
                  <Typography>
                    {patient.insurance_provider}
                    {patient.insurance_id && ` (${patient.insurance_id})`}
                  </Typography>
                </Box>
              )}
              {patient.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography>{patient.notes}</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Meetings Statistics */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{
        mb: 3,
        mx: { xs: 0.5, sm: 0 }
      }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary">
              {meetings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Meetings
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="success.main">
              {completedMeetings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="info.main">
              {activeMeetings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upcoming
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="warning.main">
              {otherMeetings.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cancelled/No-show
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Meetings and Activity Logs */}
      <Paper sx={{ mx: { xs: 0.5, sm: 0 } }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Meeting History (${meetings.length})`} />
          <Tab label="Files" />
          <Tab label="Activity Logs" />
        </Tabs>

        {/* Tab Panel 0: Meeting History */}
        {currentTab === 0 && (
          <>
            {/* Mobile Card View for Meetings */}
            {isMobile ? (
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            {meetings.length > 0 ? (
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {meetings
                  .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime())
                  .map((meeting) => (
                    <Grid item xs={12} key={meeting.id}>
                      <Card sx={{ position: 'relative' }}>
                        <CardContent sx={{ pb: 1 }}>
                          {/* Menu button */}
                          <IconButton
                            onClick={(e) => handleMenuClick(e, meeting)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>

                          {/* Header with date only */}
                          <Box display="flex" alignItems="center" mb={2} sx={{ pr: 5 }}>
                            <EventIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
                            <Box>
                              <Button
                                variant="text"
                                sx={{
                                  p: 0,
                                  fontSize: '0.875rem',
                                  fontWeight: 'medium',
                                  textTransform: 'none',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => navigate(`/meetings/${meeting.id}`)}
                              >
                                {formatDateTime(meeting.scheduled_at)}
                              </Button>
                            </Box>
                          </Box>

                          {/* Meeting Details */}
                          <Box sx={{ pl: 4 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              👩‍⚕️ {meeting.therapist.full_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              ⏱️ {meeting.duration_minutes} minutes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              📋 {meeting.session_type || 'Regular session'}
                            </Typography>

                            {/* Status and button row */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" color="text.secondary">
                                  Status:
                                </Typography>
                                <Chip
                                  label={meeting.status.toUpperCase()}
                                  color={getStatusColor(meeting.status) as any}
                                  size="small"
                                  onClick={(e) => handleStatusClick(e, meeting)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/meetings/${meeting.id}`)}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                View Details
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  No meetings found for this patient
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* Desktop Table View */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Therapist</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Session Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {meetings.length > 0 ? (
                  meetings
                    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime())
                    .map((meeting) => (
                      <TableRow key={meeting.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Button
                              variant="text"
                              sx={{
                                p: 0,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={() => navigate(`/meetings/${meeting.id}`)}
                            >
                              {formatDateTime(meeting.scheduled_at)}
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>{meeting.duration_minutes} min</TableCell>
                        <TableCell>{meeting.therapist.full_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={meeting.status.toUpperCase()}
                            color={getStatusColor(meeting.status) as any}
                            size="small"
                            onClick={(e) => handleStatusClick(e, meeting)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>{meeting.session_type || 'Regular'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => handleMenuClick(e, meeting)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No meetings found for this patient
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
            )}
          </>
        )}

        {/* Tab Panel 1: Activity Logs */}
        {currentTab === 2 && (
          <Box sx={{ p: 2 }}>
            {/* Search and Filter Controls */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={activityLogSearch}
                onChange={(e) => {
                  setActivityLogSearch(e.target.value);
                  setActivityLogPage(0);
                }}
                sx={{ flexGrow: 1, minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityLogType}
                  label="Activity Type"
                  onChange={(e) => {
                    setActivityLogType(e.target.value as ActivityType | '');
                    setActivityLogPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="patient_created">Patient Created</MenuItem>
                  <MenuItem value="patient_updated">Patient Updated</MenuItem>
                  <MenuItem value="patient_finished">Patient Finished</MenuItem>
                  <MenuItem value="patient_revived">Patient Revived</MenuItem>
                  <MenuItem value="meeting_created">Meeting Created</MenuItem>
                  <MenuItem value="meeting_updated">Meeting Updated</MenuItem>
                  <MenuItem value="meeting_status_changed">Meeting Status Changed</MenuItem>
                  <MenuItem value="meeting_text_created">Meeting Text Created</MenuItem>
                  <MenuItem value="meeting_text_updated">Meeting Text Updated</MenuItem>
                  <MenuItem value="meeting_text_deleted">Meeting Text Deleted</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {activityLogsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {activityLogsError}
              </Alert>
            )}

            {/* Activity Logs Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Activity Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Meeting</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLogsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : activityLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No activity logs found for this patient
                      </TableCell>
                    </TableRow>
                  ) : (
                    activityLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell>{log.user_name || `User #${log.user_id}`}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.activity_type
                              .split('_')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')}
                            color={
                              log.activity_type === 'patient_created' || log.activity_type.includes('created')
                                ? 'success'
                                : log.activity_type.includes('updated')
                                ? 'info'
                                : log.activity_type.includes('deleted')
                                ? 'error'
                                : log.activity_type.includes('finished')
                                ? 'warning'
                                : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.description}</TableCell>
                        <TableCell>
                          {log.meeting_title || (log.meeting_id ? `Meeting #${log.meeting_id}` : '-')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={activityLogTotal}
                rowsPerPage={activityLogRowsPerPage}
                page={activityLogPage}
                onPageChange={(_, newPage) => setActivityLogPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setActivityLogRowsPerPage(parseInt(event.target.value, 10));
                  setActivityLogPage(0);
                }}
              />
            </TableContainer>
          </Box>
        )}

        {/* Tab Panel 2: Files */}
        {currentTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Files ({files.length})
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploadingFile}
              >
                {uploadingFile ? 'Uploading...' : 'Upload File'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileUpload}
                  accept="*/*"
                />
              </Button>
            </Box>

            {filesLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : files.length === 0 ? (
              <Typography color="text.secondary" align="center" py={4}>
                No files uploaded yet
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {files.map((file) => (
                      <TableRow key={file.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FileIcon color="action" />
                            {file.file_name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {(file.file_size / 1024).toFixed(2)} KB
                        </TableCell>
                        <TableCell>{file.uploaded_by_name}</TableCell>
                        <TableCell>
                          {formatDateTime(file.created_at)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleFileDownload(file.id)}
                            title="Download"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleFileDelete(file.id)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Edit Patient Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmitPatientEdit)} noValidate>
          <DialogTitle>
            Edit Patient
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('first_name', { required: 'First name is required' })}
                  fullWidth
                  label="First Name"
                  margin="normal"
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('last_name', { required: 'Last name is required' })}
                  fullWidth
                  label="Last Name"
                  margin="normal"
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  fullWidth
                  label="Email (Optional)"
                  type="email"
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email?.message as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('phone')}
                  fullWidth
                  label="Phone"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('date_of_birth', { required: false })}
                  fullWidth
                  label="Date of Birth (Optional)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ required: false }}
                  margin="normal"
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth?.message as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    {...register('status')}
                    label="Status"
                    defaultValue={patient?.status || 'active'}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="finished">Finished</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('emergency_contact_name')}
                  fullWidth
                  label="Emergency Contact Name"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('emergency_contact_phone')}
                  fullWidth
                  label="Emergency Contact Phone"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('insurance_provider')}
                  fullWidth
                  label="Insurance Provider"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('insurance_id')}
                  fullWidth
                  label="Insurance ID"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('notes')}
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Update Patient
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Meeting Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <EventIcon sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditMeeting}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
      </Menu>

      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        {selectedMeeting?.status === 'active' && (
          <>
            <MenuItem onClick={() => handleUpdateStatus('done')}>Mark as Done</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('cancelled')}>Cancel</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('noshow')}>Mark as No-Show</MenuItem>
          </>
        )}
        {(selectedMeeting?.status === 'noshow' || selectedMeeting?.status === 'cancelled' || selectedMeeting?.status === 'done') && (
          <MenuItem onClick={() => handleUpdateStatus('active')}>Reactivate</MenuItem>
        )}
      </Menu>

      {/* All Summaries Dialog */}
      <Dialog
        open={summariesDialogOpen}
        onClose={() => setSummariesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">All Meeting Summaries</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={handleDownloadSummaries}
              disabled={summariesLoading || !allSummaries}
            >
              Download
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {summariesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                direction: 'rtl',
                textAlign: 'right',
                whiteSpace: 'pre-wrap',
                padding: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: isMobile ? '60vh' : '500px',
                overflowY: 'auto',
                backgroundColor: 'background.paper',
              }}
            >
              {allSummaries.split('\n').map((line, index) => {
                // Check if line ends with : (section title)
                const isSectionTitle = line.trim().endsWith(':') && line.trim().length > 1;
                return (
                  <Typography
                    key={index}
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      fontWeight: isSectionTitle ? 'bold' : 'normal',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {line || '\u00A0'}
                  </Typography>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummariesDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDetailPage;